import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography,
  Chip,
  Slide,
  IconButton,
  Tooltip
} from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import TuneIcon from '@mui/icons-material/Tune';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { notificationService } from '../../services/notification.service';

const NotificationTicker = () => {
  const [notifications, setNotifications] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();

  const fetchNotifications = async () => {
    try {
      const response = await notificationService.getNotifications();
      if (response && Array.isArray(response)) {
        setNotifications(response);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (notifications.length > 0) {
      const timer = setInterval(() => {
        setCurrentIndex((prevIndex) => 
          prevIndex === notifications.length - 1 ? 0 : prevIndex + 1
        );
      }, 5000); // Change notification every 5 seconds

      return () => clearInterval(timer);
    }
  }, [notifications.length]);

  const formatTopic = (topic) => {
    const parts = topic.split('.');
    return parts[parts.length - 1].replace(/_/g, ' ').toUpperCase();
  };

  const isProductRecall = (topic) => {
    return topic.includes('PRODUCT_SAFETY_RECALL');
  };

  if (notifications.length === 0) {
    return null;
  }

  const currentNotification = notifications[currentIndex];
  const isRecall = isProductRecall(currentNotification.topic);

  return (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center'
    }}>
      <Paper 
        elevation={0} 
        sx={{ 
          width: '95%',
          p: 1.5,
          mb: 3,
          backgroundColor: isRecall ? 'warning.light' : 'primary.light',
          color: isRecall ? 'warning.contrastText' : 'primary.contrastText'
        }}
      >
        <Slide direction="left" in={true} mountOnEnter unmountOnExit>
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
            <Typography variant="body2" sx={{ minWidth: 120 }}>
              {formatDistanceToNow(new Date(currentNotification.timestamp))} ago
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                flexGrow: 1,
                mx: 2,
                fontWeight: 'medium'
              }}
            >
              {currentNotification.filter_name}
              {currentNotification.action && (
                <Box component="span" sx={{ ml: 1 }}>
                  <Typography 
                    component="span" 
                    variant="body2" 
                    sx={{ 
                      color: isRecall ? 'warning.contrastText' : 'primary.contrastText',
                      fontWeight: 'medium'
                    }}
                  >
                    • {currentNotification.action.description}
                  </Typography>
                  {currentNotification.action.url && (
                    <RouterLink
                      to={currentNotification.action.url.replace('http://localhost:3000', '')}
                      style={{ 
                        marginLeft: '8px',
                        color: isRecall ? 'warning.contrastText' : 'primary.contrastText',
                        textDecoration: 'underline'
                      }}
                    >
                      View Details
                    </RouterLink>
                  )}
                </Box>
              )}
            </Typography>
            
            <Box display="flex" alignItems="center" gap={1}>
              <Chip 
                label={formatTopic(currentNotification.topic)}
                size="small"
                sx={{ 
                  backgroundColor: isRecall ? 'warning.dark' : 'primary.dark',
                  color: 'primary.contrastText'
                }}
              />
              {currentNotification.notification_count > 1 && (
                <Chip 
                  label={`+${currentNotification.notification_count - 1}`}
                  size="small"
                  sx={{ 
                    backgroundColor: isRecall ? 'warning.dark' : 'primary.dark',
                    color: 'primary.contrastText'
                  }}
                />
              )}
            </Box>
          </Box>
        </Slide>
      </Paper>

      {/* Configuration Button */}
      <Tooltip title="Manage Notification Subscriptions">
        <IconButton
          size="small"
          onClick={() => navigate('/notifications/subscriptions')}
          sx={{ 
            color: 'inherit',
            '&:hover': { 
              backgroundColor: 'rgba(255, 255, 255, 0.1)' 
            }
          }}
        >
          <TuneIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default NotificationTicker; 